import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import EmbeddedStripePaymentElement from '../components/EmbeddedStripePaymentElement';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, AlertCircle, X, FileDown, ArrowLeft } from 'lucide-react';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const ClientV2InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/portal/invoices/${invoiceId}`);
      if (res.ok) setInvoice(await res.json());
      else setError(`Failed to load invoice (${res.status})`);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [authFetch, invoiceId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      const res = await authFetch(`/api/portal/invoices/payment/status/${sessionId}`);
      if (res.ok) {
        const d = await res.json();
        setPaymentStatus(d);
        if (d.payment_status === 'paid') setTimeout(load, 800);
      }
    })();
  }, [sessionId, authFetch, load]);

  // Payment Element redirect-back handler — Stripe appends
  // ?payment_intent=&payment_intent_client_secret=&redirect_status= when
  // 3DS or another redirect-required method requires returning to this page.
  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    if (!redirectStatus) return;
    if (redirectStatus === 'succeeded') {
      setPaymentStatus({ payment_status: 'paid' });
      setTimeout(load, 800);
    } else if (redirectStatus === 'failed') {
      setError('Payment was declined. Please try again with a different method.');
    }
  }, [searchParams, load]);

  // Create a Stripe PaymentIntent for the V2-themed Payment Element.
  const createPaymentIntent = useCallback(async () => {
    const res = await authFetch('/api/portal/invoices/pay-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoice_id: invoiceId, origin_url: window.location.origin }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Could not start payment');
    }
    return await res.json();
  }, [authFetch, invoiceId]);

  const downloadPdf = async () => {
    try {
      const res = await authFetch(`/api/portal/invoices/${invoiceId}/pdf`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Could not download invoice PDF');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice ${invoice?.servemanager_job_number || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Could not download invoice PDF');
    }
  };

  const closePayment = () => { setShowPay(false); load(); };

  const rawBalance = parseFloat(invoice?.balance_due || 0);
  const isPaid = invoice && (invoice.portal_paid || rawBalance === 0);
  const total = parseFloat(invoice?.total || 0);
  // Client sees a sticky-paid invoice as fully settled regardless of the SM balance.
  const balance = invoice?.portal_paid ? 0 : rawBalance;
  const paid = invoice?.portal_paid ? (parseFloat(invoice?.total_paid || 0) || total) : parseFloat(invoice?.total_paid || 0);

  return (
    <PortalV2Layout
      crumbs={
        <span>
          <Link to="/portal/invoices" style={{ color: 'inherit' }}>Invoices</Link>
          <span style={{ margin: '0 8px', opacity: 0.5 }}>/</span>
          <strong>#{invoice?.servemanager_job_number || invoice?.id?.slice(0,8) || invoiceId}</strong>
        </span>
      }
    >
      <Link to="/portal/invoices" className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16, color: 'var(--pv2-text-muted)' }}>
        <ArrowLeft size={11} /> Back to invoices
      </Link>

      {loading && <div className="pv2-card" style={{ padding: 40, textAlign: 'center' }}>Loading…</div>}
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
      {paymentStatus?.payment_status === 'paid' && (
        <div className="pv2-alert pv2-alert-green"><CheckCircle size={14} /><span>Payment received. Receipt sent to your email.</span></div>
      )}

      {!loading && !error && invoice && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24, paddingBottom: 24, borderBottom: '2px solid var(--pv2-red)', marginBottom: 32 }}>
            <div>
              <div className="pv2-page-eyebrow" style={{ marginBottom: 12 }}>Invoice</div>
              <Link
                to={`/portal/jobs/${invoice.job_id || invoice.servemanager_job_number}`}
                className="pv2-page-title pv2-invoice-num-link"
                data-testid="pv2-invoice-num"
                title="View job details"
              >
                #{invoice.servemanager_job_number || invoice.id?.slice(0,8)}
              </Link>
            </div>
            <div>
              {isPaid
                ? <span className="pv2-pill" style={{ color: 'var(--pv2-green-text)', fontSize: 12 }}>Paid</span>
                : <span className="pv2-pill" style={{ color: 'var(--pv2-red-bright)', fontSize: 12 }}>Open</span>}
            </div>
          </div>

          {invoice.job_context && (
            <div className="pv2-card" style={{ marginBottom: 24 }} data-testid="pv2-invoice-context">
              <div className="pv2-card-head">
                <h2 className="pv2-card-title">Case Details</h2>
              </div>
              <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(0, 2fr)', gap: '20px 80px', fontSize: 13, alignItems: 'start' }}>
                {/* LEFT COLUMN — Recipient, Case Number, Service Status, Court */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {invoice.job_context.recipient_name && (
                    <div>
                      <div className="pv2-mono-sm">Recipient</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{invoice.job_context.recipient_name}</div>
                    </div>
                  )}
                  {invoice.job_context.case_number && (
                    <div>
                      <div className="pv2-mono-sm">Case Number</div>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>{invoice.job_context.case_number}</div>
                    </div>
                  )}
                  {invoice.job_context.service_status && (
                    <div>
                      <div className="pv2-mono-sm">Service Status</div>
                      <div style={{ color: invoice.job_context.service_status === 'Served' ? 'var(--pv2-green-text)' : 'var(--pv2-amber-text)' }}>
                        {invoice.job_context.service_status}
                        {invoice.job_context.served_at && (
                          <span style={{ color: 'var(--pv2-text-muted)', fontSize: 12, marginLeft: 6 }}>
                            on {fmtDate(invoice.job_context.served_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {invoice.job_context.court && (
                    <div>
                      <div className="pv2-mono-sm">Court</div>
                      <div>{invoice.job_context.court}</div>
                    </div>
                  )}
                </div>

                {/* RIGHT COLUMN — Plaintiff stacked over Defendant, wide so names breathe */}
                {(invoice.job_context.plaintiff || invoice.job_context.defendant) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {invoice.job_context.plaintiff && (
                      <div>
                        <div className="pv2-mono-sm">Plaintiff</div>
                        <div>{invoice.job_context.plaintiff}</div>
                      </div>
                    )}
                    {invoice.job_context.defendant && (
                      <div>
                        <div className="pv2-mono-sm">Defendant</div>
                        <div>{invoice.job_context.defendant}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Full-width row at bottom */}
                {invoice.job_context.service_address && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div className="pv2-mono-sm">Service Address</div>
                    <div>
                      {invoice.job_context.service_address.address1}
                      {invoice.job_context.service_address.address2 && `, ${invoice.job_context.service_address.address2}`}
                      {' · '}
                      {invoice.job_context.service_address.city}, {invoice.job_context.service_address.state} {invoice.job_context.service_address.postal_code}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
            <div className="pv2-card">
              <div className="pv2-card-head"><h2 className="pv2-card-title">Line items</h2></div>
              <table className="pv2-table" style={{ border: 0 }}>
                <thead>
                  <tr><th>Description</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Rate</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                </thead>
                <tbody>
                  {(invoice.line_items || []).map((li, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{li.name || li.description}</div>
                        {li.name && li.description && li.name !== li.description && (
                          <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 2 }}>{li.description}</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{Math.round(parseFloat(li.quantity || 1))}</td>
                      <td style={{ textAlign: 'right' }}>{fmtUSD(li.unit_cost || li.rate)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtUSD(li.total || li.amount || (parseFloat(li.unit_cost || 0) * parseFloat(li.quantity || 1)))}</td>
                    </tr>
                  ))}
                  {(!invoice.line_items || invoice.line_items.length === 0) && (
                    <tr><td colSpan={4} className="pv2-table-empty">No line items.</td></tr>
                  )}
                </tbody>
              </table>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: 16, borderTop: '1px solid var(--pv2-ink-line)', gap: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', gap: 24, fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700 }}><span>Total</span><span>{fmtUSD(total)}</span></div>
                {paid > 0 && <div style={{ display: 'flex', gap: 24, color: 'var(--pv2-green-text)' }}><span>Paid</span><span>−{fmtUSD(paid)}</span></div>}
                <div style={{ display: 'flex', gap: 24, fontSize: 16, fontFamily: 'Playfair Display, serif', fontWeight: 700, paddingTop: 8, borderTop: '1px solid var(--pv2-ink-line)', width: '100%', justifyContent: 'flex-end', color: balance > 0 ? 'var(--pv2-red-bright)' : 'var(--pv2-green-text)' }}>
                  <span>Balance Due</span><span>{fmtUSD(balance)}</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {!isPaid && (
                <button onClick={() => setShowPay(true)} className="pv2-btn pv2-btn-primary" style={{ width: '100%', padding: '16px' }} data-testid="pv2-invoice-pay">
                  Pay {fmtUSD(balance)}
                </button>
              )}
              <button
                type="button"
                onClick={downloadPdf}
                className="pv2-btn pv2-btn-ghost"
                style={{ width: '100%' }}
                data-testid="pv2-invoice-download"
              >
                <FileDown size={14} /> Download PDF
              </button>
              <div className="pv2-card">
                <div className="pv2-card-head"><h3 className="pv2-card-title" style={{ fontSize: 14 }}>Details</h3></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                  <div>
                    <div className="pv2-mono-sm">Issued</div>
                    <div>{fmtDate(invoice.issued_on)}</div>
                  </div>
                  <div>
                    <div className="pv2-mono-sm">Terms</div>
                    <div>NET30</div>
                  </div>
                  {invoice.paid_on && (
                    <div>
                      <div className="pv2-mono-sm">Paid</div>
                      <div style={{ color: 'var(--pv2-green-text)' }}>{fmtDate(invoice.paid_on)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showPay && invoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }}>
          <div style={{ width: '100%', maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, color: '#fff' }}>
              <span className="pv2-mono">Complete payment · {fmtUSD(balance)}</span>
              <button onClick={closePayment} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="pv2-payment-close">
                <X size={16} />
              </button>
            </div>
            <div style={{ background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)' }} data-testid="pv2-payment-modal">
              <EmbeddedStripePaymentElement
                createIntent={createPaymentIntent}
                returnUrl={`${window.location.origin}/payment-success`}
                onSuccess={(pi) => {
                  closePayment();
                  navigate(`/payment-success?payment_intent_id=${pi?.id || ''}&continue=${encodeURIComponent('/portal/invoices/' + invoiceId)}`);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </PortalV2Layout>
  );
};

export default ClientV2InvoiceDetail;
